import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskProductsSameCategoryBlockConfig } from '@front-platform/content-builder';
import { kioskProductsSameCategoryBlockResolveConfig } from '@front-platform/content-builder';
import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import { ProductStoreSelector } from '@common/selectors/product.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ProductsService } from '@core/services/products/products.service';
import { DoodCategoryWithProduct } from '@shared/interfaces/category.interface';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import { map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
export interface KioskProductsSameCategoryBlockRuntimeConfig {}

export type KioskProductsSameCategoryBlockConfigInput = Partial<
  Omit<KioskProductsSameCategoryBlockConfig, keyof KioskProductsSameCategoryBlockRuntimeConfig> &
    KioskProductsSameCategoryBlockRuntimeConfig
>;
export type KioskProductsSameCategoryBlockResolvedConfig = Omit<
  KioskProductsSameCategoryBlockConfig,
  keyof KioskProductsSameCategoryBlockRuntimeConfig
> &
  Partial<KioskProductsSameCategoryBlockRuntimeConfig>;
export interface KioskProductsSameCategoryBlockContract extends ConfigComponentContract<
  KioskProductsSameCategoryBlockConfigInput,
  KioskProductsSameCategoryBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-products-same-category-block',
  templateUrl: './kiosk-products-same-category-block.component.html',
  standalone: false,
})
export class KioskProductsSameCategoryBlockComponent
  implements OnInit, OnDestroy, KioskProductsSameCategoryBlockContract
{
  readonly config = input<KioskProductsSameCategoryBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskProductsSameCategoryBlockConfig,
    KioskProductsSameCategoryBlockRuntimeConfig
  >(kioskProductsSameCategoryBlockResolveConfig, () => ({ config: this.config() }));
  heading = computed(() => this.resolvedConfig()['heading'] as unknown as string);
  primaryButtonBackgroundColor = computed(
    () =>
      this.resolvedConfig()[
        'primary_button_background_color'
      ] as unknown as IContentBuilderFieldColor,
  );
  primaryButtonTextColor = computed(
    () =>
      this.resolvedConfig()['primary_button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  secondaryButtonBackgroundColor = computed(
    () => this.resolvedConfig()['secondary_button_background_color'] as unknown as string,
  );
  secondaryButtonTextColor = computed(
    () => this.resolvedConfig()['secondary_button_text_color'] as unknown as string,
  );
  textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  backgroundColor = computed(() => this.resolvedConfig()['background_color'] as unknown as string);

  @ViewChild('carousel') carousel!: ElementRef;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly productSelector = inject(ProductStoreSelector);
  private readonly shopRefiner = inject(ShopStoreRefiner);
  private readonly productsService = inject(ProductsService);
  private destroyed$ = new Subject<boolean>();
  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);

  protected readonly optionCardType = computed(() => {
    const type = this.kioskGlobalSettingsService.settings().productCard.styles.type;
    return type === 'defaultCard' ? '' : type;
  });

  scrollAmount = 172; // w-40 + mr-3

  categoryWithProducts$?: Observable<DoodCategoryWithProduct | null>;
  products$?: Observable<DoodProductModel[] | null>;

  ngOnInit(): void {
    this.products$ = this.productSelector.selectActiveCategory.pipe(
      takeUntil(this.destroyed$),
      switchMap(categoryId => {
        if (categoryId) {
          return this.shopRefiner.selectCategoryById(categoryId).pipe(
            switchMap((category: DoodCategoryWithProduct | null) => {
              if (category && this.isProductIncluded(category)) {
                return this.findProducts(category?.products);
              } else {
                return this.getProductsFromProductCategory();
              }
            }),
          );
        } else {
          return this.getProductsFromProductCategory();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  isProductIncluded(category: DoodCategoryWithProduct): boolean {
    const activeProduct = this.productSelector.selectActive();

    if (activeProduct && category.products.includes(activeProduct?.id)) {
      return true;
    } else {
      return false;
    }
  }

  getProductsFromProductCategory(): Observable<DoodProductModel[] | null> {
    return this.shopRefiner.selectCategories.pipe(
      switchMap((categories: DoodCategoryWithProduct[] | null) => {
        if (categories) {
          let foundCategory = categories.find(category => {
            const activeProduct = this.productSelector.selectActive();
            if (activeProduct) {
              return category.products.includes(activeProduct.id);
            }
            return false;
          });

          if (foundCategory) {
            return this.findProducts(foundCategory.products);
          }
        }
        return of(null);
      }),
    );
  }

  navigateToProduct(product: DoodProductModel): void {
    const shopCity = this.route.snapshot.paramMap.get('shopCity');
    const shopType = this.route.snapshot.paramMap.get('shopType');
    const shopSlug = this.route.snapshot.paramMap.get('shopSlug');

    this.router.navigate([
      `/fr/${shopCity}/${shopType}/${shopSlug}/product/${product.id}/product-add`,
    ]);
  }

  private getProductIdFromRoute(route: ActivatedRoute): string | null {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.paramMap.get('productId');
  }

  scrollLeft() {
    this.carousel.nativeElement.scrollLeft -= this.scrollAmount;
  }

  scrollRight() {
    this.carousel.nativeElement.scrollLeft += this.scrollAmount;
  }

  findProducts(productsId: string[]): Observable<DoodProductModel[] | null> {
    return this.productsService.selectAll$().pipe(
      map(products =>
        products.filter(
          product =>
            productsId.includes(product.id) && product.id != this.getProductIdFromRoute(this.route),
        ),
      ),
      map(products => {
        if (products.length === 0) {
          return null;
        }
        return products;
      }),
    );
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskProductsSameCategoryBlockComponent, {
  'keyAliases': {},
});
